/* eslint-disable complexity */
/* eslint-disable class-methods-use-this */

// eslint-disable-next-line no-unused-vars
import React, {
    useState, useEffect, useRef, useCallback
} from 'react';
import ReactDOM from 'react-dom';
import AddressActions from 'actions/AddressActions';
import LanguageLocale from 'utils/LanguageLocale';
import utilityApi from 'services/api/utility';
import store from 'store/Store';
import ErrorsUtils from 'utils/Errors';
import ErrorConstants from 'utils/ErrorConstants';
import HelperUtils from 'utils/Helpers';
import FormValidator from 'utils/FormValidator';
import Debounce from 'utils/Debounce';
import keyConsts from 'utils/KeyConstants';
import addressUtils from 'utils/Address';
import helperUtils from 'utils/Helpers';
import ErrorList from 'components/ErrorList';
import { colors, forms, space } from 'style/config';
import { mediaQueries } from 'style/config';
import FrictionlessUtils from 'utils/FrictionlessCheckout';

import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Grid } from 'components/ui';
import NameFields from 'components/Addresses/FrictionlessAddressForm/components/NameFields';
import CountrySelect from 'components/Addresses/FrictionlessAddressForm/components/CountrySelect';
import PhoneField from 'components/Addresses/FrictionlessAddressForm/components/PhoneField';
import EmailField from 'components/Addresses/FrictionlessAddressForm/components/EmailField';
import FirstAddressField from 'components/Addresses/FrictionlessAddressForm/components/FirstAddressField';
import SecondAddressField from 'components/Addresses/FrictionlessAddressForm/components/SecondAddressField';
import Avs from 'components/Addresses/FrictionlessAddressForm/components/Avs';
import PostalCodeField from 'components/Addresses/FrictionlessAddressForm/components/PostalCodeField';
import CityField from 'components/Addresses/FrictionlessAddressForm/components/CityField';
import StateProvinceField from 'components/Addresses/FrictionlessAddressForm/components/StateProvinceField';
import { useLoqateAddresses } from 'hooks/useLoqateAddresses';
import { useCountryStateManagement } from 'hooks/useCountryStateManagement';

const DEBOUNCE_ADDRESS = 200;
let debounceSubscription;
const GRID_GUTTER = 4;
const { isCountryInternational, COUNTRIES } = LanguageLocale;
const { sanitizeUSAddress, sanitizeUSAddressTwo, sanitizeInput } = helperUtils;

const countyInitialData = (address, country) => {
    const international = isCountryInternational(country || COUNTRIES.US);
    const rawPhone = address?.phone || '';
    const formattedPhone = rawPhone ? (international ? rawPhone : FormValidator.getFormattedPhoneNumber(rawPhone)) : '';

    return {
        ...address,
        country: country || COUNTRIES.US,
        formattedPhone: formattedPhone
    };
};

const isFCReleaseThreeEnabled = FrictionlessUtils.isFCReleaseThreeEnabled();

function FrictionlessAddressForm(props) {
    const [address, setAddress] = useState(() => countyInitialData(props.address, props.country));
    const [errorMessages] = useState('');
    const [showAddress2Input, setShowAddress2Input] = useState(
        props.showAddress2Input ? props.showAddress2Input : props.address && props.address.address2
    );
    const [hasAddress1Focus, setHasAddress1Focus] = useState(false);
    const [isAddressVerified, setIsAddressVerified] = useState(props.address && props.address.isAddressVerified);
    const [isAddressModified, setIsAddressModified] = useState(false);
    const [isAdsRestricted] = useState(props.isAdsRestricted);
    const [isInternational, setIsInternational] = useState(false);
    const [hasLoqateError, setHasLoqateError] = useState(false);

    const firstNameInputRef = useRef(null);
    const lastNameInputRef = useRef(null);
    const addressInputRef = useRef(null);
    const address2InputRef = useRef(null);
    const zipCodeInputRef = useRef(null);
    const cityInputRef = useRef(null);
    const stateSelectRef = useRef(null);
    const regionInputRef = useRef(null);
    const phoneNumberInputRef = useRef(null);
    const emailInputRef = useRef(null);
    const countrySelectRef = useRef(null);
    const addressPredictionRef = useRef(null);
    const loqateWaitingForRef = useRef('');

    const hasAVS = useCallback(() => {
        const { isBillingAddress, hasAVS: hasAVSProp = true } = props;

        return Sephora.configurationSettings.enableAddressValidation && !isInternational && !isBillingAddress && hasAVSProp;
    }, [props.isBillingAddress, props.hasAVS, isInternational]);

    const updateEditStore = useCallback(
        (name, value) => {
            const editStore = store.getState().editData[props.editStore];
            props.updateEditData(Object.assign({}, editStore, { [name]: value }), props.editStore);

            hasAVS() && verifyLoqateAddressState(name, value);
        },
        [props.editStore, hasAVS]
    );

    const {
        stateList,
        countryInvalid,
        setStateListData,
        displayCityStateInputs,
        zipCodeInvalid,
        setDisplayCityStateInputs,
        setZipCodeInvalid,
        cityStateZipInvalid,
        setCityStateZipInvalid,
        handleCityOnChange,
        handleStateSelect,
        handleCountrySelect
    } = useCountryStateManagement({
        isEditMode: props.isEditMode,
        updateEditStore,
        onCountryChange: props.onCountryChange,
        state: address.state,
        setAddress,
        countrySelectRef,
        stateSelectRef,
        displayCityStateInputsprop: props.address && props.address.postalCode,
        setIsInternational
    });

    const updateLoqateVerifiedAddressState = useCallback(
        ({
            addressLine1, addressLine2, city, postalCode, stateLocal, isPOBoxAddress, addressType
        }) => {
            updateEditStore('address1', addressLine1);
            updateEditStore('address2', addressLine2);

            if (addressLine2) {
                setShowAddress2Input(true);
            }

            updateEditStore('city', city);
            updateEditStore('postalCode', postalCode);
            updateEditStore('state', stateLocal);
            updateEditStore('isPOBoxAddress', isPOBoxAddress);
            updateEditStore('addressType', addressType);

            setDisplayCityStateInputs(true);
            setIsAddressVerified(true);
            setZipCodeInvalid(false);
            setCityStateZipInvalid(false);

            // Clear any Loqate selection error when user selects from dropdown
            setHasLoqateError(false);
            addressInputRef.current?.showError(null);

            props.onSubmitLoqateAddress();
        },
        [
            updateEditStore,
            props.onSubmitLoqateAddress,
            setShowAddress2Input,
            setDisplayCityStateInputs,
            setIsAddressVerified,
            setZipCodeInvalid,
            setCityStateZipInvalid
        ]
    );

    const setFieldFocus = useCallback(node => {
        clearTimeout(debounceSubscription);
        ReactDOM.findDOMNode(node).querySelector('input').focus();
    }, []);

    const blurAddress1 = useCallback(() => {
        addressInputRef.current?.setValue(address.address1);

        setHasAddress1Focus(false);
        setLoqateAddresses([]);
    }, [address.address1]);

    const handleAddress1Blur = useCallback(() => {
        debounceSubscription = Debounce.debounce.call(this, blurAddress1, DEBOUNCE_ADDRESS)();
    }, [blurAddress1]);

    const section = props.isGiftCardSection ? 'gift card' : 'deliver to';

    const {
        loqateAddresses,
        highlightedAddrIndex,
        getLoqateAddresses,
        setLoqateAddresses,
        setHighlightedAddrIndex,
        handleLoqateAddressClick,
        getHighlight
    } = useLoqateAddresses({
        address,
        loqateWaitingForRef,
        addressPredictionRef,
        updateLoqateVerifiedAddressState,
        setFieldFocus,
        handleAddress1Blur,
        addressInputRef,
        section
    });

    const renderAVS = useCallback(() => {
        return (
            <Avs
                loqateAddresses={loqateAddresses}
                hasAddress1Focus={hasAddress1Focus}
                highlightedAddrIndex={highlightedAddrIndex}
                localization={props.localization}
                onMouseLeaveList={() => setHighlightedAddrIndex(-1)}
                onMouseEnterItem={setHighlightedAddrIndex}
                onClickItem={handleLoqateAddressClick}
                getHighlight={getHighlight}
                addressPredictionRef={comp => (addressPredictionRef.current = comp)}
            />
        );
    }, [loqateAddresses, hasAddress1Focus, highlightedAddrIndex, props.localization]);

    const componentWillUnmount = useCallback(() => {
        props.clearEditData(props.editStore);
        clearTimeout(debounceSubscription);
    }, [props.editStore]);

    const formatZipPostalCode = useCallback(
        (inputData, divider, leftLength, totalLength) => {
            const formatZipPostal = addressUtils.formatZipPostalCode(inputData, divider, leftLength, totalLength);
            updateEditStore('postalCode', formatZipPostal);

            return formatZipPostal;
        },
        [updateEditStore]
    );

    const autoFillCityAndState = useCallback(
        zipCode => {
            utilityApi
                .getStateAndCityForZipCode(address.country, zipCode)
                .then(data => {
                    setDisplayCityStateInputs(true);
                    setZipCodeInvalid(null);
                    updateEditStore('state', data.state);
                    updateEditStore('city', HelperUtils.titleCase(data.city));
                })
                .catch(() => {
                    updateEditStore('state', '');
                    updateEditStore('city', '');
                    setDisplayCityStateInputs(false);
                    setZipCodeInvalid('Please enter a valid zipcode.');
                });
        },
        [address.country, updateEditStore]
    );

    const handleZipCodeOnChange = useCallback(
        e => {
            const value = e.target.value;
            const currentPostalCode = address.postalCode;

            if (value === currentPostalCode) {
                return;
            }

            if (address.country === COUNTRIES.US) {
                const zipCode = formatZipPostalCode(value, '-', 5, 9);

                if (zipCode && !zipCodeInvalid && (zipCode.length === 5 || zipCode.length === 10) && !address.state && !address.city) {
                    autoFillCityAndState(zipCode);
                } else if (!zipCode || (zipCode && zipCode.length < 5)) {
                    if (address.state || address.city) {
                        updateEditStore('state', '');
                        updateEditStore('city', '');
                    }

                    setZipCodeInvalid(null);
                    setCityStateZipInvalid(null);
                    setDisplayCityStateInputs(false);
                } else if (zipCode && zipCode.length === 5 && zipCode !== currentPostalCode.slice(0, 5)) {
                    autoFillCityAndState(zipCode);
                }
            } else if (address.country === COUNTRIES.CA) {
                const postalCode = formatZipPostalCode(value.toUpperCase(), ' ', 3, 6);

                if (postalCode && postalCode.length === 7) {
                    autoFillCityAndState(postalCode);
                } else if (!postalCode || (postalCode && postalCode.length < 7)) {
                    if (address.state || address.city) {
                        updateEditStore('state', '');
                        updateEditStore('city', '');
                    }

                    setZipCodeInvalid(null);
                    setCityStateZipInvalid(null);
                    setDisplayCityStateInputs(false);
                }
            } else {
                updateEditStore('postalCode', value.toUpperCase());
            }
        },
        [address, zipCodeInvalid, formatZipPostalCode, autoFillCityAndState, updateEditStore]
    );

    const getFormattedPhoneNumber = useCallback(
        rawValue => {
            if (isInternational) {
                return rawValue;
            }

            return FormValidator.getFormattedPhoneNumber(rawValue);
        },
        [isInternational]
    );

    const handlePhoneChange = useCallback(
        e => {
            const isPhoneAutofilled = phoneNumberInputRef.current?.inputElementRef?.matches(':-webkit-autofill');
            const currentValue = phoneNumberInputRef.current?.inputElementRef?.value || '';
            const rawPhone = currentValue.replace(HelperUtils.specialCharacterRegex, '');
            const formattedPhone = getFormattedPhoneNumber(rawPhone);
            updateEditStore('phone', rawPhone);
            updateEditStore('formattedPhone', formattedPhone);

            if (isPhoneAutofilled) {
                requestAnimationFrame(() => {
                    setAddress(prev => ({
                        ...prev,
                        phone: rawPhone,
                        formattedPhone: formattedPhone
                    }));

                    // Force the input to show the formatted value
                    if (phoneNumberInputRef.current?.setValue) {
                        phoneNumberInputRef.current.setValue(formattedPhone);
                    }
                });

                return;
            }

            formatPhoneNumber(e);
        },
        [getFormattedPhoneNumber, updateEditStore]
    );

    const formatPhoneNumber = useCallback(
        e => {
            const rawValue = e.target.value.replace(HelperUtils.specialCharacterRegex, '');
            const formattedPhone = getFormattedPhoneNumber(rawValue);

            setAddress(prev => ({
                ...prev,
                phone: rawValue,
                formattedPhone: formattedPhone
            }));
        },
        [getFormattedPhoneNumber]
    );

    useEffect(() => {
        if (!phoneNumberInputRef.current?.inputElementRef) {
            return;
        }

        // Use requestAnimationFrame to defer cursor positioning until after React's batched updates
        requestAnimationFrame(() => {
            const inputElement = phoneNumberInputRef.current?.inputElementRef;

            if (!inputElement) {
                return;
            }

            // Only manipulate cursor if this input is currently focused
            // This prevents issues when multiple forms are present (e.g., Gift Card + merch)
            if (document.activeElement !== inputElement) {
                return;
            }

            let additionalSpacesAddedByFormatter = 0;
            const currentCursorPosition = inputElement.selectionStart;

            if (address?.phone?.length === 4) {
                additionalSpacesAddedByFormatter = 3;
            } else if (address?.phone?.length === 7) {
                additionalSpacesAddedByFormatter = 1;
            }

            const newPosition = currentCursorPosition + additionalSpacesAddedByFormatter;
            // Use setSelectionRange instead of setting selectionEnd directly for better iOS compatibility
            inputElement.setSelectionRange(newPosition, newPosition);
        });
    }, [address?.phone, address?.formatPhoneNumber]);

    const validateForm = useCallback(
        (doNotClearErrors, shouldDispatchErrors = true) => {
            const fieldsForValidation = [];

            // if user is on checkout and is editing a billing address
            // there is no firstName or lastName inputs, so no need to validate
            if (props.isBillingAddress && props.isCheckout) {
                fieldsForValidation.push(addressInputRef.current, zipCodeInputRef.current, cityInputRef.current);
            } else {
                fieldsForValidation.push(
                    firstNameInputRef.current,
                    lastNameInputRef.current,
                    addressInputRef.current,
                    zipCodeInputRef.current,
                    cityInputRef.current
                );

                if (props.isGuestCheckout) {
                    fieldsForValidation.push(emailInputRef.current);
                }
            }

            if (isInternational) {
                fieldsForValidation.push(regionInputRef.current);
            }

            if (!props.isPhoneFieldHidden) {
                fieldsForValidation.push(phoneNumberInputRef.current);
            }

            if (!doNotClearErrors) {
                ErrorsUtils.clearErrors();
            }

            ErrorsUtils.collectClientFieldErrors(fieldsForValidation);

            const isFieldsValid = !ErrorsUtils.validate(fieldsForValidation, shouldDispatchErrors);

            // Check if Loqate selection is required but not made
            let loqateError = false;

            if (props.isShortenedLoqateForm && !isAddressVerified && address.address1) {
                // User typed in shortened Loqate form but didn't select from dropdown
                loqateError = true;
                setHasLoqateError(true);

                if (shouldDispatchErrors) {
                    // Show error with minimal message for accessibility
                    const errorMessage = props.loqateSelectionError || '';
                    addressInputRef.current?.showError(errorMessage);
                }
            } else {
                setHasLoqateError(false);
            }

            return isFieldsValid && !loqateError;
        },
        [props, isInternational, isAddressVerified, address.address1]
    );

    const handleResponseError = useCallback((message, value, errorKey) => {
        const cityInvalid = errorKey === ErrorConstants.ERROR_CODES.LOOKUP_CITY_INVALID && message;
        const stateInvalid = errorKey === ErrorConstants.ERROR_CODES.LOOKUP_STATE_INVALID && message;
        const zipCodeInvalidValue = errorKey === ErrorConstants.ERROR_CODES.LOOKUP_POSTAL_CODE_INVALID && message;
        const locationError = cityInvalid || stateInvalid || zipCodeInvalidValue;

        if (locationError) {
            setCityStateZipInvalid(locationError);

            return true; // returns true if any of these errors are handled by this form
        }

        switch (errorKey) {
            case ErrorConstants.ERROR_CODES.ADDRESS1_INCORRECT:
                addressInputRef.current.showError(message, value, errorKey);

                return true;
            case ErrorConstants.ERROR_CODES.ADDRESS2_INCORRECT:
                address2InputRef.current.showError(message, value, errorKey);

                return true;
            case ErrorConstants.ERROR_CODES.FIRST_NAME_INCORRECT:
                firstNameInputRef.current.showError(message, value, errorKey);

                return true;
            case ErrorConstants.ERROR_CODES.LAST_NAME_INCORRECT:
                lastNameInputRef.current.showError(message, value, errorKey);

                return true;
            default:
        }

        return false;
    }, []);

    const getData = useCallback(() => {
        if (address.country === COUNTRIES.CA) {
            address.postalCode.toUpperCase();
        }

        const addressData = {
            address: {
                firstName: address.firstName,
                lastName: address.lastName,
                address1: address.address1,
                address2: address.address2,
                postalCode: address.postalCode,
                city: address.city,
                state: address.state,
                country: address.country
            },
            isPOBoxAddress: isAddressVerified ? address.isPOBoxAddress : undefined,
            addressType: isAddressVerified ? address.addressType : undefined
        };

        if (!props.isPhoneFieldHidden) {
            addressData.address.phone = address.phone.replace(HelperUtils.specialCharacterRegex, '');
        }

        if (props.isGuestCheckout) {
            addressData.address.shipEmail = address.email;
        }

        if (props.isEditMode) {
            addressData.address.addressId = address.addressId;
        }

        return addressData;
    }, [address, isAddressVerified, props]);

    const handleAddress2Change = useCallback(
        e => {
            const sanitizedValue = sanitizeUSAddressTwo(e.target.value);
            address2InputRef.current?.setValue(sanitizedValue);
            updateEditStore(e.target.name, sanitizedValue);
        },
        [updateEditStore]
    );

    const handleAddress2SectionClick = useCallback(() => {
        setShowAddress2Input(true);
        setFieldFocus(address2InputRef.current);
    }, [setFieldFocus]);

    const handleAddress1Focus = useCallback(() => {
        addressInputRef.current?.setValue(address.address1);
        setHasAddress1Focus(true);
    }, [address.address1]);

    // Check if we make the Loqate Address dirty by manually modyfing addresses fields

    const verifyLoqateAddressState = useCallback(
        (field, value) => {
            let initialFieldValue;

            switch (field) {
                case 'address1':
                case 'address2':
                case 'postalCode':
                case 'city':
                case 'state':
                    initialFieldValue = props.address && props.address[field];

                    if (initialFieldValue !== value) {
                        setIsAddressVerified(false);
                        setIsAddressModified(true);
                    }

                    break;
                default:
                    break;
            }
        },
        [props.address]
    );

    const handleAddress1Change = useCallback(
        value => {
            const sanitizedValue = sanitizeUSAddress(value);
            loqateWaitingForRef.current = sanitizedValue;

            if (!props.shouldHiddenLoqate) {
                getLoqateAddresses(sanitizedValue);
            }
        },
        [props.shouldHiddenLoqate, getLoqateAddresses]
    );

    const handleAddress1ChangeDebounced = useCallback(
        e => Debounce.debounce(handleAddress1Change(e.target.value), DEBOUNCE_ADDRESS),
        [handleAddress1Change]
    );

    const handleAddress1KeyUp = useCallback(
        e => {
            const value = sanitizeUSAddress(e.target.value);
            const fieldName = e.target.name;
            addressInputRef.current?.setValue(value);

            updateEditStore(fieldName, value);

            // return early if AVS shouldn't be displayed
            if (!hasAVS()) {
                return;
            }

            switch (e.key) {
                case keyConsts.UP:
                case keyConsts.DOWN:
                case keyConsts.ENTER:
                    e.preventDefault();

                    return;
                case keyConsts.ESC:
                    e.preventDefault();
                    getLoqateAddresses(value);

                    return;
                default:
                    return;
            }
        },
        [updateEditStore, hasAVS, getLoqateAddresses]
    );

    const handleFnameBlur = useCallback(() => {
        const value = sanitizeInput(firstNameInputRef.current?.getValue());
        firstNameInputRef.current?.setValue(value);
    }, []);

    const handleLnameBlur = useCallback(() => {
        const value = sanitizeInput(lastNameInputRef.current?.getValue());

        lastNameInputRef.current?.setValue(value);
    }, []);

    const handleAddress1KeyDown = useCallback(
        e => {
            let index = highlightedAddrIndex;

            switch (e.key) {
                case keyConsts.TAB:
                    return;
                case keyConsts.ESC:
                    loqateWaitingForRef.current = '';
                    e.target.value = '';

                    return;
                case keyConsts.UP:
                    if (index <= 0) {
                        index = loqateAddresses.length - 1;
                    } else {
                        index--;
                    }

                    break;
                case keyConsts.DOWN:
                    if (index === -1 || index >= loqateAddresses.length - 1) {
                        index = 0;
                    } else {
                        index++;
                    }

                    break;
                case keyConsts.ENTER:
                    if (loqateAddresses[index]) {
                        e.preventDefault();
                        handleLoqateAddressClick(loqateAddresses[index]);
                    }

                    return;
                default:
                    !hasAddress1Focus && handleAddress1Focus();

                    return;
            }

            e.preventDefault();
            !hasAddress1Focus && handleAddress1Focus();
            setHighlightedAddrIndex(index, true);
        },
        [highlightedAddrIndex, loqateAddresses, hasAddress1Focus, handleLoqateAddressClick, handleAddress1Focus, setHighlightedAddrIndex]
    );

    const isAddressVerifiedFunc = useCallback(() => {
        return isAddressVerified;
    }, [isAddressVerified]);

    const isAddressModifiedFunc = useCallback(() => {
        return isAddressModified;
    }, [isAddressModified]);

    const getHasLoqateError = useCallback(() => {
        return hasLoqateError;
    }, [hasLoqateError]);

    useEffect(() => {
        //TODO. this is a trick. once we have react 18+ we can use useImperativeHandle  --- and forwardRef
        props.onRef({
            validateForm,
            getData,
            hasAVS,
            isAddressVerified: isAddressVerifiedFunc,
            isAddressModified: isAddressModifiedFunc,
            hasLoqateError: getHasLoqateError,
            handleResponseError,
            firstNameInput: firstNameInputRef.current,
            lastNameInput: lastNameInputRef.current,
            addressInput: addressInputRef.current,
            address2Input: address2InputRef.current,
            zipCodeInput: zipCodeInputRef.current,
            cityInput: cityInputRef.current,
            stateSelect: stateSelectRef.current,
            regionInput: regionInputRef.current,
            phoneNumberInput: phoneNumberInputRef.current,
            emailInput: emailInputRef.current
        });
    }, [validateForm, getData, hasAVS, isAddressVerifiedFunc, isAddressModifiedFunc, getHasLoqateError, handleResponseError]);

    useEffect(() => {
        setAddress(prev => ({ ...prev, ...props.editStoreData }));
    }, [props.editStoreData]);

    useEffect(() => {
        loqateWaitingForRef.current = '';

        props.isCheckout && firstNameInputRef.current && firstNameInputRef.current.focus();

        if (!props.isEditMode && props.biAccount) {
            const { firstName, lastName, homePhone } = props.biAccount;
            setAddress(prev => ({
                ...prev,
                firstName: firstName,
                lastName: lastName,
                phone: homePhone || '',
                formattedPhone: getFormattedPhoneNumber(homePhone || '')
            }));
        }

        return componentWillUnmount;
    }, [setAddress, props.isEditMode, props.biAccount, componentWillUnmount, getFormattedPhoneNumber]);

    useEffect(() => {
        // Initialize edit store with address prop on mount
        const country = address.country;

        if (isInternational && props.isEditMode) {
            updateEditStore('state', props.address.state);
        } else if (!isInternational) {
            AddressActions.getStateList(country, states => setStateListData(states, country));
        }
    }, [updateEditStore, props.address?.state, address?.country]);

    const {
        isPhoneFieldHidden,
        isBillingAddress,
        isCheckout,
        isCountryFieldHidden,
        isShortenedLoqateForm,
        isFirstNameFieldDisabled,
        isLastNameFieldDisabled,
        fireFormErrorAnalytics,
        modalTitle,
        isSubscription,
        disableCountryList
    } = props;

    const {
        localization: { enterZipCodeText }
    } = props;

    const isUS = address.country === COUNTRIES.US;

    return (
        <div>
            <ErrorList errorMessages={errorMessages} />
            {isCheckout && isBillingAddress ? null : (
                <NameFields
                    firstName={address.firstName}
                    lastName={address.lastName}
                    isFirstNameFieldDisabled={isFirstNameFieldDisabled}
                    isLastNameFieldDisabled={isLastNameFieldDisabled}
                    isAdsRestricted={isAdsRestricted}
                    isFCReleaseThreeEnabled={isFCReleaseThreeEnabled}
                    isSubscription={isSubscription}
                    modalTitle={modalTitle}
                    localization={props.localization}
                    onFirstNameChange={updateEditStore}
                    onLastNameChange={updateEditStore}
                    onFirstNameBlur={handleFnameBlur}
                    onLastNameBlur={handleLnameBlur}
                    firstNameRef={comp => (firstNameInputRef.current = comp)}
                    lastNameRef={comp => (lastNameInputRef.current = comp)}
                    fireFormErrorAnalytics={fireFormErrorAnalytics}
                />
            )}
            <Grid
                gap={[0, GRID_GUTTER]}
                gridTemplateColumns={['1fr 1fr']}
            >
                {isCountryFieldHidden || (
                    <div css={styles.fullField}>
                        <CountrySelect
                            country={address.country}
                            countryInvalid={countryInvalid}
                            countryList={props.countryList}
                            isBillingAddress={isBillingAddress}
                            disableCountryList={disableCountryList}
                            localization={props.localization}
                            onChange={handleCountrySelect}
                            countrySelectRef={comp => (countrySelectRef.current = comp)}
                            isUS={isUS}
                        />
                    </div>
                )}
                {isPhoneFieldHidden || (
                    <div css={styles.fullField}>
                        <PhoneField
                            phone={address.phone}
                            formattedPhone={address.formattedPhone}
                            isInternational={isInternational}
                            phoneInvalid={false} // Note: phoneInvalid was not used in original, assuming false
                            isFCReleaseThreeEnabled={isFCReleaseThreeEnabled}
                            isSubscription={isSubscription}
                            modalTitle={modalTitle}
                            localization={props.localization}
                            onChange={isFCReleaseThreeEnabled ? handlePhoneChange : formatPhoneNumber}
                            phoneNumberRef={comp => (phoneNumberInputRef.current = comp)}
                            fireFormErrorAnalytics={fireFormErrorAnalytics}
                        />
                    </div>
                )}
                {props.shouldDisplayEmailInputField && (
                    <div css={styles.fullField}>
                        <EmailField
                            email={address.email}
                            isAdsRestricted={isAdsRestricted}
                            isFCReleaseThreeEnabled={isFCReleaseThreeEnabled}
                            localization={props.localization}
                            onChange={e => updateEditStore(e.target.name, e.target.value)}
                            emailInputRef={comp => (emailInputRef.current = comp)}
                        />
                    </div>
                )}
            </Grid>
            <Grid gridTemplateColumns={[null, '1fr 1fr']}>
                <FirstAddressField
                    address1={address.address1}
                    isAdsRestricted={isAdsRestricted}
                    isAddressVerified={isAddressVerified}
                    hasAVS={hasAVS()}
                    isShortenedLoqateForm={isShortenedLoqateForm}
                    highlightedAddrIndex={highlightedAddrIndex}
                    hasAddress1Focus={hasAddress1Focus}
                    hasLoqateError={hasLoqateError}
                    shouldHiddenLoqate={props.shouldHiddenLoqate}
                    isFCReleaseThreeEnabled={isFCReleaseThreeEnabled}
                    isSubscription={isSubscription}
                    modalTitle={modalTitle}
                    localization={props.localization}
                    onKeyUp={handleAddress1KeyUp}
                    onBlur={handleAddress1Blur}
                    onFocus={handleAddress1Focus}
                    onKeyDown={handleAddress1KeyDown}
                    onChange={handleAddress1ChangeDebounced}
                    addressInputRef={comp => (addressInputRef.current = comp)}
                    fireFormErrorAnalytics={fireFormErrorAnalytics}
                    renderAVS={renderAVS}
                />
            </Grid>
            {!isShortenedLoqateForm && (
                <div>
                    <SecondAddressField
                        address2={address.address2}
                        showAddress2Input={showAddress2Input}
                        isAdsRestricted={isAdsRestricted}
                        isFCReleaseThreeEnabled={isFCReleaseThreeEnabled}
                        isSubscription={isSubscription}
                        modalTitle={modalTitle}
                        localization={props.localization}
                        onChange={handleAddress2Change}
                        onKeyUp={handleAddress2Change}
                        onAddAddress2Click={handleAddress2SectionClick}
                        address2InputRef={comp => (address2InputRef.current = comp)}
                        fireFormErrorAnalytics={fireFormErrorAnalytics}
                    />
                    <Grid
                        gridTemplateColumns={'1fr 1fr'}
                        gap={GRID_GUTTER}
                    >
                        <PostalCodeField
                            postalCode={address.postalCode}
                            country={address.country}
                            isInternational={isInternational}
                            isAdsRestricted={isAdsRestricted}
                            zipCodeInvalid={zipCodeInvalid}
                            cityStateZipInvalid={cityStateZipInvalid}
                            isFCReleaseThreeEnabled={isFCReleaseThreeEnabled}
                            isSubscription={isSubscription}
                            modalTitle={modalTitle}
                            localization={props.localization}
                            onChange={handleZipCodeOnChange}
                            onKeyDown={FormValidator.inputAcceptOnlyNumbers}
                            zipCodeInputRef={comp => (zipCodeInputRef.current = comp)}
                            fireFormErrorAnalytics={fireFormErrorAnalytics}
                        />
                        {!displayCityStateInputs && (
                            <Box
                                display='flex'
                                height={forms.HEIGHT}
                                alignItems='center'
                                lineHeight='tight'
                                fontSize={['sm', null]}
                            >
                                {enterZipCodeText}
                            </Box>
                        )}
                    </Grid>
                    <Grid
                        gap={GRID_GUTTER}
                        rowGap={0}
                        gridTemplateColumns={['1fr 1fr', displayCityStateInputs ? '1fr 1fr' : '25% 1fr 1fr']}
                    >
                        {(isInternational || displayCityStateInputs) && (
                            <Box gridRow={['2/2', '1/1']}>
                                <CityField
                                    city={address.city}
                                    cityStateZipInvalid={cityStateZipInvalid}
                                    isFCReleaseThreeEnabled={isFCReleaseThreeEnabled}
                                    isSubscription={isSubscription}
                                    modalTitle={modalTitle}
                                    localization={props.localization}
                                    onChange={handleCityOnChange}
                                    cityInputRef={comp => (cityInputRef.current = comp)}
                                    fireFormErrorAnalytics={fireFormErrorAnalytics}
                                />
                            </Box>
                        )}
                        <Box gridRow={['2/2', '1/1']}>
                            <StateProvinceField
                                state={address.state}
                                country={address.country}
                                stateList={stateList}
                                isInternational={isInternational}
                                displayCityStateInputs={displayCityStateInputs}
                                cityStateZipInvalid={cityStateZipInvalid}
                                isFCReleaseThreeEnabled={isFCReleaseThreeEnabled}
                                isSubscription={isSubscription}
                                modalTitle={modalTitle}
                                localization={props.localization}
                                onChange={handleStateSelect}
                                stateSelectRef={comp => (stateSelectRef.current = comp)}
                                regionInputRef={comp => (regionInputRef.current = comp)}
                                fireFormErrorAnalytics={fireFormErrorAnalytics}
                            />
                        </Box>
                    </Grid>
                </div>
            )}
        </div>
    );
}

const styles = {
    avsItem: {
        display: 'flex',
        alignItems: 'center',
        padding: space[3],
        cursor: 'pointer',
        '& + *': {
            borderTop: `1px solid ${colors.lightGray}`
        }
    },
    avsItemActive: {
        position: 'relative',
        backgroundColor: colors.nearWhite
    },
    avsItemInner: {
        flex: 1
    },
    avsItemDesc: {
        color: colors.gray
    },
    fullField: {
        [mediaQueries.xsMax]: {
            gridColumn: '1 / -1'
        }
    },
    searchIcon: {
        position: 'absolute',
        top: 13,
        right: space[3],
        pointerEvents: 'none'
    }
};

export default wrapFunctionalComponent(FrictionlessAddressForm, 'FrictionlessAddressForm');
